import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import {
  Reporting,
  ScorePoint,
  Scale,
  Report,
  IndividualReporting,
  ParticipantCategoryNames,
  FreeTextReport,
  Table,
  Score,
  ScoreType,
  IndividualBriefReport,
  IndividualDetailReport,
} from '../models/reporting';
import { map } from 'rxjs/operators';
import { ReportProcess } from '../models/reporting/report-process.model';
import { EnumDescription } from '../helpers';
import { ProcessReportModel } from '../user/my-bu/bu-reports/process-report/process-report.mode';
import { getWithoutTime, parseUtcDate } from '../helpers/date-helper';

@Injectable()
export class ReportingService {
  downloadIndividualPdfClicked$: Observable<boolean>;
  downloadSummaryPdfClicked$: Observable<boolean>;
  detailedReportClicked$: Observable<boolean>;
  individualReportDownloaded$: Observable<boolean>;
  summaryReportDownloaded$: Observable<boolean>;
  private apiController = 'api/reporting';
  private downloadIndividualPdfSubject = new Subject<boolean>();
  private downloadSummaryPdfSubject = new Subject<boolean>();
  private detailedReportClickedSubject = new Subject<boolean>();
  private individualReportDownloadedSubject = new Subject<boolean>();
  private summaryReportDownloadedSubject = new Subject<boolean>();
  private requestOptions: Object = {
    headers: new HttpHeaders({
      Accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    responseType: 'blob',
  };
  private readonly translationKeyPrefix = 'lang.reporting.';
  constructor(private httpService: HttpClient) {
    this.downloadIndividualPdfClicked$ = this.downloadIndividualPdfSubject.asObservable();
    this.downloadSummaryPdfClicked$ = this.downloadSummaryPdfSubject.asObservable();
    this.individualReportDownloaded$ = this.individualReportDownloadedSubject.asObservable();
    this.summaryReportDownloaded$ = this.summaryReportDownloadedSubject.asObservable();
    this.detailedReportClicked$ = this.detailedReportClickedSubject.asObservable();
  }

  getIndividualAppraiseeExcelReport(
    processId: number,
    processDurationAppraiseeId: number
  ): Observable<Blob> {
    return this.httpService.get<Blob>(
      `${this.apiController}/individual/excel/${processId}/${processDurationAppraiseeId}`,
      this.requestOptions
    );
  }

  getUserReports(employeeId: number): Observable<Array<ReportProcess>> {
    return this.httpService.get<Array<ReportProcess>>(
      `${this.apiController}/userReports/${employeeId}`
    );
  }

  getIndividualReportData(
    processId: number,
    appraiseeId: number
  ): Observable<IndividualReporting> {
    return this.httpService
      .get(`${this.apiController}/individual/${processId}/${appraiseeId}`)
      .pipe(map(this.mapServerResponse));
  }

  getCombinedDetailReportData(
    processId: number
  ): Observable<IndividualDetailReport> {
    return this.httpService.get(
      `${this.apiController}/individual/detailed/admin/${processId}`
    ) as Observable<IndividualDetailReport>;
  }

  getSummaryExcelReport(processId: number): Observable<Blob> {
    return this.httpService.get<Blob>(
      `${this.apiController}/summary/${processId}`,
      this.requestOptions
    );
  }

  getCrossprocessSummaryExcelReport(processIds: Array<number>) {
    return this.httpService.post<Blob>(
      `${this.apiController}/summary/crossprocess`,
      processIds,
      this.requestOptions
    );
  }

  getProcessExcelReport(model: ProcessReportModel) {
    return this.httpService.post<Blob>(
      `${this.apiController}/my-bu/report`,
      this.mapReportParams(model),
      this.requestOptions
    );
  }
  
  getProcessInfoReport(model: ProcessReportModel) {
    return this.httpService.post<Blob>(
        `api/process/hrProcessInfo`,
        this.mapReportParams(model),
        this.requestOptions
    );
  }

  getIndividualBriefReport(
    processId: number,
    appraiseeId: number
  ): Observable<IndividualBriefReport> {
    return this.httpService.get(
      `${this.apiController}/individual/brief/${processId}/${appraiseeId}`
    ) as Observable<IndividualBriefReport>;
  }

  getIndividualDetailedReport(
    processId: number,
    appraiseeId: number
  ): Observable<IndividualDetailReport> {
    return this.httpService.get(
      `${this.apiController}/individual/detailed/${processId}/${appraiseeId}`
    ) as Observable<IndividualDetailReport>;
  }

  swipeReport(isDetailedReportClicked: boolean) {
    this.detailedReportClickedSubject.next(isDetailedReportClicked);
  }

  generatePdf(htmlContent: string): Observable<Blob> {
    const formData: FormData = new FormData();
    formData.append('htmlString', htmlContent);
    return this.httpService
      .post(`${this.apiController}/generatePdf`, formData, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((x) => x.body));
  }

  onIndividualPdfDownloadClick() {
    this.downloadIndividualPdfSubject.next(true);
  }

  onIndividualPdfDownloaded() {
    this.individualReportDownloadedSubject.next(true);
  }

  onSummaryPdfDownloadClick() {
    this.downloadSummaryPdfSubject.next(true);
  }

  onSummaryPdfDownloaded() {
    this.summaryReportDownloadedSubject.next(true);
  }

  getParticipantCategoryTitle(participantCategoryNameType: number): string {
    return (
      this.translationKeyPrefix +
      EnumDescription.GetDescription(
        ParticipantCategoryNames,
        participantCategoryNameType
      )
    );
  }

  updateAppraiseeReport(processDurationAppraiseeId: number): Observable<void> {
    return this.httpService.get<void>(
      `${this.apiController}/updateReportData/${processDurationAppraiseeId}`
    ) as Observable<void>;
  }

  getScoreType = (value: number): ScoreType => {
    switch (value) {
      case 7:
        return 'assessing-yourself';
      case 8:
        return 'comparison-group';
      default:
        return 'assessing-you';
    }
  };
  private mapReportParams(model: ProcessReportModel) {
    return {
      BusinessUnitId: model.BusinessUnit ? model.BusinessUnit.id : null,
      EmployeeId: model.Employee,
      Stages: model.Stages,
      StartDate: parseUtcDate(getWithoutTime(model.StartDate)),
      EndDate: parseUtcDate(getWithoutTime(model.EndDate)),
    };
  }

  private mapServerResponse = (reporting: any): IndividualReporting => {
    return {
      header: {
        userReportingInfo: {
          fullName: reporting.appraiseeName,
          startDate: reporting.startDate,
          details: reporting.name,
        },
        descriptionLocalized: 'lang.reporting.individualReportDescription',
        respondentsDistribution: this.getSortedByType(
          reporting.participantCategories
        ).map(
          (pc) =>
            ({
              title: pc.name,
              participantCategoryNameType: pc.participantCategoryType,
              value: pc.respondentCount,
              votes: pc.participantCount,
            } as ScorePoint)
        ),
      },
      consolidatedReporting: reporting.scale
        ? this.getConsolidatedReporting(
            reporting.summarySections,
            reporting.scale
          )
        : null,
      detailedReporting: this.getDetailedReporting(reporting.detailedSections),
    } as IndividualReporting;
  };

  private getConsolidatedReporting = (
    sections,
    scale
  ): Reporting<'Consolidated'> => {
    return {
      legend: {
        title: 'Consolidated report',
        descriptionLocalized: 'lang.reporting.consolidatedReportingDescription',
        items: [
          {
            type: 'assessing-you',
            description: 'Assessing you by other respondents',
          },
          {
            type: 'assessing-yourself',
            description: 'Evaluation of yourself',
          },
        ],
      },
      reports: sections.map((s) => this.getConsolidatedReport(s, scale)),
    };
  };

  private getConsolidatedReport = (section, scale): Report<'Consolidated'> => {
    return {
      detalization: 'Consolidated',
      title: section.name,
      tables: this.getConsolidatedReportTables(section, scale),
    } as Report<'Consolidated'>;
  };

  private getConsolidatedReportTables = (section, scale): Array<Table> => {
    if (section.questions) {
      return section.questions.map(this.getReportTableFromQuestion);
    }

    return section.questionCategories.map((qc) =>
      this.getReportTableFromQuestionCategories(qc, scale)
    );
  };

  private getReportTableFromQuestionCategories = (
    questionCategory,
    scale
  ): Table => {
    return {
      type: 'Linear',
      scale: {
        type: 'Text',
        values: scale.values.map(
          (v) => ({ title: v.label, value: v.value } as ScorePoint)
        ),
      } as Scale,
      isScaleShown: scale.values.length > 0,
      header: {
        question: questionCategory.name,
        value: 'Current period',
        previousPeriods: [],
      },
      body: {
        rows: this.getSortedByType(questionCategory.participantCategories).map(
          (pc) =>
            ({
              type: this.getScoreType(pc.participantCategoryType),
              category: pc.name,
              average: this.getAverage(pc),
              maxValue: scale.maxValue,
              scorePoints: pc.answers
                .filter((a) => !a.isAverage)
                .map(
                  (a) =>
                    ({
                      title: a.textValue,
                      value: a.numericValue,
                      votes: a.valueCount,
                    } as ScorePoint)
                ),
              previousPeriodValues: [],
            } as Score)
        ),
      },
    };
  };

  private getDetailedReporting = (sections): Reporting<'Detailed'> => {
    return {
      legend: {
        title: 'lang.reporting.detailedReportingTitle',
        descriptionLocalized: 'lang.reporting.detailedReportingDescription',
        items: [
          {
            type: 'assessing-you',
            description: 'lang.reporting.assessingOther',
          },
          {
            type: 'assessing-yourself',
            description: 'lang.reporting.evaluationYourself',
          },
        ],
      },
      reports: sections.filter((el) => el.name).map(this.getDetailedReport),
      freeTextReports: sections
        .filter((el) => !el.name)
        .reduce((r, el) => {
          r.push(
            ...el.questions.map(
              (q) =>
                ({
                  question: q.name,
                  answers: q.answers.map((a) => a.textValue || a.numericValue),
                } as FreeTextReport)
            )
          );
          return r;
        }, []),
    } as Reporting<'Detailed'>;
  };

  private getDetailedReport = (section): Report<'Detailed'> => {
    return {
      detalization: 'Detailed',
      title: section.name,
      tables: section.questions.map(this.getReportTableFromQuestion),
    } as Report<'Detailed'>;
  };

  private getReportTableFromQuestion = (question): Table => {
    return {
      type: question.type || 'Linear',
      scale: {
        type: 'Text',
        values: question.scale.values.map(
          (v) => ({ title: v.label, value: v.value } as ScorePoint)
        ),
      } as Scale,
      isScaleShown: question.scale.values.length > 0,
      header: {
        question: question.name,
        value: 'Current period',
        previousPeriods: [],
      },
      body: {
        rows: this.getSortedByType(question.participantCategories).map(
          (pc) =>
            ({
              type: this.getScoreType(pc.participantCategoryType),
              category: pc.name,
              average: this.getAverage(pc),
              maxValue: question.scale.maxValue,
              scorePoints: pc.answers
                .filter((a) => !a.isAverage)
                .map(
                  (a) =>
                    ({
                      title: a.textValue,
                      value: a.numericValue,
                      votes: a.valueCount,
                    } as ScorePoint)
                ),
              previousPeriodValues: [],
            } as Score)
        ),
      },
    } as Table;
  };

  private getSortedByType(array: Array<any>) {
    array.sort((a, b) => a.participantCategoryType - b.participantCategoryType);
    return array;
  }

  private getAverage(pc) {
    const averageValue = pc.answers.find((a) => a.isAverage).numericValue;
    return Math.round(averageValue * 10) / 10;
  }
}
